/**
 * Primitives for reading an untrusted response body.
 *
 * **A response body is untrusted input.** A browser can be pointed at any host, so every
 * field core sends is parsed rather than cast. A parser that does not recognise a value
 * fails rather than degrading it: a desk that quietly rendered a half-understood payload
 * would be showing a founder the wrong review state, the wrong balance or the wrong rank,
 * which is worse than showing nothing (canon `AGENTS.md` § 12).
 *
 * These live apart from the readers that use them so the seller desk and the auction panel
 * cannot drift into two different ideas of what a valid integer or a valid string is.
 */
import { ApiError } from "./client";

/** A sentinel distinct from `null`, so "absent" and "malformed" never collapse into one. */
export const INVALID = Symbol("invalid");
export type Parsed<T> = T | typeof INVALID;

export function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/** An optional string field: absent, or a real string. Anything else is malformed. */
export function optionalString(value: unknown): Parsed<string | null> {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") return INVALID;
  return value.trim() === "" ? null : value.trim();
}

/**
 * Money, ranks and counts arrive as non-negative integers. A float, a numeric string or a
 * negative is refused rather than rounded or clamped — core states amounts in integer cents
 * and ranks as whole positions, so a value that is not one did not come from the contract
 * this app is reading.
 */
export function optionalCount(value: unknown): Parsed<number | null> {
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) return INVALID;
  return value;
}

export function requiredCount(value: unknown): Parsed<number> {
  const parsed = optionalCount(value);
  return parsed === INVALID || parsed === null ? INVALID : parsed;
}

/** A plain object, or `null` for anything else — including an array. */
export function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * A malformed body is reported as an error rather than as empty data.
 *
 * The status is `null` because nothing went wrong at the transport layer — the request
 * succeeded and the answer was not one this app understands. `AsyncPanel` tells those two
 * apart by the code, so a founder is never told their connection dropped when it did not.
 */
export function malformed(): ApiError {
  return new ApiError("Dealbid answered with something this page could not read.", {
    status: null,
    code: "MALFORMED_RESPONSE",
  });
}
